import { useCurrentUser } from '@/hooks/useCurrentUser';
import { alert } from '@/services/alert';
import { fetchBot } from '@/services/services';
import { Button, Flex, Group, Modal, Text } from '@mantine/core';
import { createReference, formatHumanName } from '@medplum/core';
import { useMedplum } from '@medplum/react';
import { useState } from 'react';
import { useTimelineContext } from './hooks/useTimelineContext';

interface RestrictedContentManagerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  children?: React.ReactNode;
  isRestricted: boolean;
}

export function RestrictedContentManager(props: RestrictedContentManagerProps) {
  const { size = 'xs', isRestricted, children } = props;

  const medplum = useMedplum();
  const {
    currentUserPermissions: { isAdmin },
  } = useCurrentUser();

  const { patientContext, timelineContext, documentsContext } = useTimelineContext();

  const patient = patientContext?.patient;
  const { pauseLiveUpdates: pauseTimelineLiveUpdates, refresh: refreshTimeline } = timelineContext;
  const { pauseLiveUpdates: pauseDocumentsLiveUpdates, refreshDocuments } = documentsContext ?? {};

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleButtonClick = () => {
    setIsModalOpen(true);
  };

  const handleConfirm = async () => {
    if (isLoading) return;

    setIsLoading(true);
    // We will pause live update to prevent a wave of updates from being triggered when the
    // break-glass bot is executed
    pauseTimelineLiveUpdates();
    pauseDocumentsLiveUpdates?.();
    try {
      await fetchBot(medplum, 'break-glass', {
        patient: createReference(patient!),
      });
    } catch (error) {
      alert.error(
        `We couldn't unlock the restricted content: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setIsModalOpen(false);
      setIsLoading(false);
      // We will refresh the timeline to fetch everything again and resume live updates
      refreshTimeline();
      refreshDocuments?.();
    }
  };

  const handleCancel = () => {
    if (isLoading) return;
    setIsModalOpen(false);
  };

  if (!isRestricted) {
    return children;
  }

  return (
    <>
      <Flex justify="center" w="100%" pt="xs">
        {isAdmin ? (
          <Button
            onClick={handleButtonClick}
            size={size}
            variant="outline"
            color="gold"
            style={{
              borderRadius: '20px',
              fontWeight: 500,
            }}
          >
            View sensitive information
          </Button>
        ) : (
          <Text size="sm" c="dimmed" ta="center" style={{ fontStyle: 'italic' }}>
            Sensitive information
          </Text>
        )}
      </Flex>

      <Modal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="View sensitive information"
        centered
        size="md"
        closeOnClickOutside={!isLoading}
        closeOnEscape={!isLoading}
      >
        <Text size="sm" mb="md">
          By proceeding, you will be granted access to sensitive information from{' '}
          <Text span fw={600}>
            {formatHumanName(patient?.name?.[0])}
          </Text>
          . This action will be logged for security and compliance purposes. Are you sure you want to proceed?
        </Text>

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" color="gray" onClick={handleCancel} size="sm" disabled={isLoading}>
            Cancel
          </Button>
          <Button color="red" onClick={handleConfirm} size="sm" loading={isLoading} disabled={isLoading}>
            Continue
          </Button>
        </Group>
      </Modal>
    </>
  );
}
